import { useState } from "react";

/**
 * FeedbackModal - Allows users to submit bugs, suggestions, or questions
 */
export default function FeedbackModal({
  open,
  onClose,
  employeeId,
  employeeName,
  workCenter,
  currentPage = "Time Tracker"
}) {
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError("Please enter a message");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          type,
          message: message.trim(),
          employeeId,
          employeeName,
          workCenter,
          page: currentPage,
        }),
      });

      if (!res.ok) {
        throw new Error("Failed to submit feedback");
      }

      setSubmitted(true);
      setMessage("");
      setType("bug");

      // Auto-close after showing success
      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    setType("bug");
    setError(null);
    setSubmitted(false);
    onClose();
  };

  const typeOptions = [
    { value: "bug", label: "Bug Report", icon: "🐛", color: "bg-red-600" },
    { value: "suggestion", label: "Suggestion", icon: "💡", color: "bg-yellow-600" },
    { value: "question", label: "Question", icon: "❓", color: "bg-blue-600" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      onClick={handleClose}
    >
      <div
        className="bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Send Feedback
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-900 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {submitted ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-lg font-medium text-green-400 mb-2">
                Thank you for your feedback!
              </h3>
              <p className="text-gray-500 text-sm">
                Your message has been submitted.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type Selector */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  What type of feedback?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {typeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setType(opt.value)}
                      className={`
                        flex flex-col items-center gap-1 p-3 rounded-lg border transition-all
                        ${type === opt.value
                          ? `${opt.color} border-white/30 text-gray-900`
                          : "bg-slate-100 border-gray-200 text-gray-700 hover:bg-slate-100"
                        }
                      `}
                    >
                      <span className="text-xl">{opt.icon}</span>
                      <span className="text-xs">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  {type === "bug" && "What's not working?"}
                  {type === "suggestion" && "What would you like to see improved?"}
                  {type === "question" && "What would you like to know?"}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    type === "bug"
                      ? "Describe what happened and what you expected..."
                      : type === "suggestion"
                      ? "Describe your idea..."
                      : "Ask your question..."
                  }
                  className="
                    w-full h-32 px-3 py-2
                    bg-slate-100 border border-gray-200 rounded-lg
                    text-gray-900 placeholder-gray-500
                    focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500
                    resize-none
                  "
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="text-red-400 text-sm bg-red-900/20 px-3 py-2 rounded-lg">
                  {error}
                </div>
              )}

              {/* Context Info */}
              <div className="text-xs text-gray-500">
                <span>From: {employeeName || "Anonymous"}</span>
                {workCenter && <span> | Work Center: {workCenter}</span>}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !message.trim()}
                className={`
                  w-full py-3 rounded-lg font-medium transition-all
                  ${submitting || !message.trim()
                    ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700 text-gray-900"
                  }
                `}
              >
                {submitting ? "Sending..." : "Send Feedback"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
